/**
 * Gameplan index — the body of the Gameplan panel when no plan is
 * currently active. Lists every saved gameplan with create / rename /
 * delete / pick-active affordances.
 *
 * Activating a plan (clicking the row name) sets activeGameplanId,
 * which causes GameplanPanel to swap this view out for the
 * GameplanEditor body. Coming back via the "← all gameplans" link in
 * the editor clears the id and lands the user back here.
 *
 * Inline editing pattern: each row holds a local mode ('view',
 * 'rename', 'confirm-delete'). The mode is mode-local component state
 * — it doesn't survive a panel unmount, but it doesn't need to: it's
 * a transient UI affordance, not domain state.
 */
import { useEffect, useState } from 'react';
import { useAppStore } from '../state/store';
import type {
  Counter,
  MediaRefsIndex,
  Move,
  Position,
} from '../types/domain';
import type { Gameplan } from '../types/gameplan';
import { downloadGameplanCheatsheet } from './gameplan-export';

type Props = {
  positions: Position[];
  moves: Move[];
  counters: Counter[];
  mediaRefs: MediaRefsIndex | null;
};

export function GameplanIndex({
  positions,
  moves,
  counters,
  mediaRefs,
}: Props) {
  const gameplans = useAppStore((s) => s.gameplans);
  const createGameplan = useAppStore((s) => s.createGameplan);
  const setActiveGameplan = useAppStore((s) => s.setActiveGameplan);

  // When a freshly-created plan should land in rename mode immediately
  // (so the user types the real name instead of leaving the default
  // "New Gameplan"), we mark its id here and the row picks it up.
  const [autoRenameId, setAutoRenameId] = useState<string | null>(null);

  const onCreate = () => {
    const id = createGameplan('New Gameplan');
    setAutoRenameId(id);
  };

  // Sort by updatedAt desc so the most-recently-touched plan is on
  // top. Stable sort: identical timestamps fall back to createdAt.
  const sorted = [...gameplans].sort((a, b) => {
    if (b.updatedAt !== a.updatedAt) return b.updatedAt - a.updatedAt;
    return b.createdAt - a.createdAt;
  });

  if (gameplans.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
          gap: 14,
        }}
      >
        <div style={{ fontSize: 14, opacity: 0.7 }}>No gameplans yet.</div>
        <div
          style={{
            fontSize: 12,
            opacity: 0.45,
            maxWidth: 280,
            lineHeight: 1.55,
          }}
        >
          A gameplan is a saved sequence of moves and positions you
          want to study, drill, or have ready as a cheat sheet. Create
          one to start mapping out a sequence.
        </div>
        <button
          onClick={onCreate}
          style={primaryBtnStyle}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              'rgba(155,231,255,0.18)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              'rgba(155,231,255,0.10)';
          }}
        >
          + new gameplan
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // so the inner scroller actually scrolls
      }}
    >
      {/* Section sub-header — "Saved gameplans" on the left, +new on
          the right. Keeps the create affordance accessible without
          requiring the user to scroll past empty rows. */}
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <span
          style={{
            fontSize: 11,
            letterSpacing: 0.7,
            textTransform: 'uppercase',
            opacity: 0.55,
          }}
        >
          saved
        </span>
        <button
          onClick={onCreate}
          style={smallBtnStyle}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              'rgba(155,231,255,0.18)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              'rgba(155,231,255,0.10)';
          }}
        >
          + new
        </button>
      </div>

      {/* Scrollable list of rows. */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '6px 10px 18px',
        }}
      >
        {sorted.map((gp) => (
          <GameplanRow
            key={gp.id}
            gameplan={gp}
            autoRenameOnMount={gp.id === autoRenameId}
            onAutoRenameConsumed={() => setAutoRenameId(null)}
            onActivate={() => setActiveGameplan(gp.id)}
            // Cheatsheet download from the row's ⋯ menu needs entity
            // data; thread it through so the menu is functionally
            // equivalent to the one inside an open gameplan.
            onExportCheatsheet={() =>
              downloadGameplanCheatsheet(gp, positions, moves, counters, mediaRefs)
            }
          />
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Row
// -----------------------------------------------------------------

// Two transient row modes. 'rename' is only entered via the
// auto-rename signal after "+ new" (rename is otherwise reached
// from inside the editor by clicking the title — see GameplanEditor).
// The delete confirmation now lives INSIDE the ⋯ menu (matching the
// editor's pattern) so there's no row-wide swap to a confirm bar.
type RowMode = 'view' | 'rename';

function GameplanRow({
  gameplan,
  autoRenameOnMount,
  onAutoRenameConsumed,
  onActivate,
  onExportCheatsheet,
}: {
  gameplan: Gameplan;
  autoRenameOnMount: boolean;
  onAutoRenameConsumed: () => void;
  onActivate: () => void;
  /** Trigger the HTML cheatsheet download for this plan. Wired
   *  from the parent (which holds the entity data) so the row can
   *  expose the action from its ⋯ menu without owning the args. */
  onExportCheatsheet: () => void;
}) {
  const renameGameplan = useAppStore((s) => s.renameGameplan);
  const deleteGameplan = useAppStore((s) => s.deleteGameplan);

  const [mode, setMode] = useState<RowMode>(autoRenameOnMount ? 'rename' : 'view');
  const [draftName, setDraftName] = useState(gameplan.name);
  const [hovered, setHovered] = useState(false);
  // Hamburger (⋯) menu open state. Replaces the previous hover-only
  // edit/del icon cluster — the menu is always reachable on mobile
  // (no hover needed) and consolidates the editor's exact action set
  // (PNG / cheatsheet / delete) into a single per-row affordance.
  const [menuOpen, setMenuOpen] = useState(false);
  // Inline delete confirmation lives inside the menu, mirroring the
  // editor hamburger's confirm-in-place pattern.
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Tell the parent we consumed the auto-rename signal so a future
  // re-render doesn't re-trigger it. Runs after mount, so we don't
  // setState-during-render in the parent.
  useEffect(() => {
    if (autoRenameOnMount) onAutoRenameConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Total node count across all lines — gives the user a feel for how
  // built-out a plan is at a glance.
  const totalNodes = gameplan.lines.reduce(
    (acc, l) => acc + l.nodes.length,
    0,
  );

  const lineCount = gameplan.lines.length;

  const commitRename = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== gameplan.name) {
      renameGameplan(gameplan.id, trimmed);
    }
    setMode('view');
  };
  const cancelRename = () => {
    setDraftName(gameplan.name);
    setMode('view');
  };

  // The row's visible highlight should be suppressed while the ⋯
  // menu is open — otherwise clicking the hamburger leaves the
  // entire card looking "selected" even though the user is engaging
  // with the menu, not the card. (Item #11 in the mobile UX batch.)
  const visiblyHovered = hovered && !menuOpen;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        margin: '6px 0',
        padding: '10px 12px',
        borderRadius: 8,
        background: visiblyHovered
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
        transition: 'background 100ms ease',
        cursor: mode === 'view' ? 'pointer' : 'default',
      }}
      onClick={() => {
        // Row click activates the gameplan ONLY when we're in plain
        // view mode and the click didn't originate from a button or
        // input. (React stops propagation handles that via the
        // buttons' own onClick stopPropagation — see below.)
        if (mode === 'view') onActivate();
      }}
    >
      {/* Top line: name (or rename input) + actions cluster. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {mode === 'rename' ? (
          <input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelRename();
              }
              // Stop Esc from bubbling up to the panel's close handler.
              e.stopPropagation();
            }}
            onBlur={commitRename}
            style={{
              flex: 1,
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(155,231,255,0.30)',
              borderRadius: 4,
              color: '#e6e8ef',
              fontSize: 13,
              padding: '4px 6px',
              outline: 'none',
            }}
          />
        ) : (
          <div
            style={{
              flex: 1,
              fontSize: 13,
              fontWeight: 600,
              color: '#e6e8ef',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {gameplan.name}
          </div>
        )}

        {/* Actions cluster. The old hover-only edit/del icon row was
            replaced with a single ⋯ menu — always visible (so mobile
            can reach it) and consolidating rename / export / delete
            into a consistent dropdown that mirrors the one inside an
            open gameplan editor. */}
        {mode !== 'rename' && (
          <div
            style={{
              display: 'flex',
              gap: 4,
              flexShrink: 0,
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {mode === 'view' && (
              <>
                <IconBtn
                  title="Actions"
                  onClick={() => {
                    setMenuOpen((v) => !v);
                    // Close any pending delete-confirm whenever the
                    // menu is toggled — opening fresh should never
                    // re-show the "are you sure" state.
                    setConfirmingDelete(false);
                  }}
                  aria-label="Gameplan actions"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  ⋯
                </IconBtn>
                {menuOpen && (
                  <>
                    {/* Click-away backdrop. stopPropagation prevents
                        the backdrop click from bubbling up to the
                        row's onClick (which would otherwise activate
                        the gameplan — item #11 in the mobile UX
                        batch). */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        setConfirmingDelete(false);
                      }}
                      style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                    />
                    <div
                      role="menu"
                      // Stop clicks inside the menu from reaching the
                      // row's onClick. The wrapping div already does
                      // this but the menu lives inside that scope and
                      // we want defence-in-depth.
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        right: 0,
                        zIndex: 41,
                        minWidth: 176,
                        background: 'rgba(20,22,28,0.98)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {/* Items mirror the editor's hamburger 1:1 —
                          PNG, cheatsheet, divider, delete-with-
                          confirm. Per the user's spec (#12), the
                          index hamburger should be EXACTLY the same
                          as the editor's, minus Rename (renaming
                          happens inside the editor via title click). */}
                      <button
                        role="menuitem"
                        disabled
                        title="Open this gameplan to download a specific line as PNG"
                        style={{
                          ...menuItemStyle,
                          color: '#6b7280',
                          cursor: 'not-allowed',
                        }}
                      >
                        Download line PNG
                      </button>
                      <button
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          onExportCheatsheet();
                        }}
                        style={menuItemStyle}
                      >
                        Download cheatsheet
                      </button>
                      <div
                        style={{
                          height: 1,
                          background: 'rgba(255,255,255,0.07)',
                          margin: '2px 4px',
                        }}
                      />
                      {confirmingDelete ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            role="menuitem"
                            onClick={() => {
                              deleteGameplan(gameplan.id);
                              setConfirmingDelete(false);
                              setMenuOpen(false);
                            }}
                            style={{
                              ...menuItemStyle,
                              flex: 1,
                              textAlign: 'center',
                              background: 'rgba(255,128,140,0.18)',
                              border: '1px solid rgba(255,128,140,0.40)',
                              color: '#ffc0c8',
                            }}
                          >
                            Confirm
                          </button>
                          <button
                            role="menuitem"
                            onClick={() => setConfirmingDelete(false)}
                            style={{
                              ...menuItemStyle,
                              flex: 1,
                              textAlign: 'center',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          role="menuitem"
                          onClick={() => setConfirmingDelete(true)}
                          style={{
                            ...menuItemStyle,
                            color: '#ffaab2',
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Bottom line: small meta — N lines · M nodes · updated. */}
      <div
        style={{
          marginTop: 4,
          fontSize: 11,
          opacity: 0.5,
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span>
          {lineCount} {lineCount === 1 ? 'line' : 'lines'}
        </span>
        <span>·</span>
        <span>
          {totalNodes} {totalNodes === 1 ? 'node' : 'nodes'}
        </span>
        <span>·</span>
        <span>{formatRelative(gameplan.updatedAt)}</span>
      </div>

      {gameplan.description && mode === 'view' && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            opacity: 0.55,
            lineHeight: 1.45,
            // 2-line clamp so unusually-long descriptions don't blow
            // out the row height in the index.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {gameplan.description}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// Small UI bits
// -----------------------------------------------------------------

function IconBtn({
  children,
  onClick,
  title,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  danger?: boolean;
}) {
  return (
    <button
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        background: danger
          ? 'rgba(255,128,140,0.10)'
          : 'rgba(255,255,255,0.06)',
        border: `1px solid ${
          danger ? 'rgba(255,128,140,0.30)' : 'rgba(255,255,255,0.10)'
        }`,
        color: danger ? '#ffc0c8' : '#cdd2da',
        borderRadius: 4,
        padding: '3px 7px',
        fontSize: 10,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = danger
          ? 'rgba(255,128,140,0.20)'
          : 'rgba(255,255,255,0.12)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = danger
          ? 'rgba(255,128,140,0.10)'
          : 'rgba(255,255,255,0.06)';
      }}
    >
      {children}
    </button>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  marginTop: 4,
  background: 'rgba(155,231,255,0.10)',
  color: '#cdf6ff',
  border: '1px solid rgba(155,231,255,0.30)',
  borderRadius: 6,
  padding: '7px 16px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background 120ms ease, border-color 120ms ease',
};

const smallBtnStyle: React.CSSProperties = {
  background: 'rgba(155,231,255,0.10)',
  color: '#cdf6ff',
  border: '1px solid rgba(155,231,255,0.30)',
  borderRadius: 4,
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background 120ms ease',
};

/** Shared dropdown menu-item style — matches the in-editor menu so
 *  the two contexts feel like the same control. */
const menuItemStyle: React.CSSProperties = {
  textAlign: 'left',
  background: 'transparent',
  color: '#e6e8ef',
  border: 'none',
  borderRadius: 4,
  padding: '7px 10px',
  fontSize: 12,
  cursor: 'pointer',
  width: '100%',
};

// -----------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------

/**
 * Compact relative time: "just now", "5m ago", "2h ago", "yesterday",
 * "3d ago", or an absolute MMM D for anything older than ~a week.
 * Designed to fit comfortably in the row's meta line.
 */
function formatRelative(ts: number): string {
  const now = Date.now();
  const diffMs = now - ts;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  const d = new Date(ts);
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}
